import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Tooltip, IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const StatusChangesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    current_page: 1,
    per_page: 10
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [action, setAction] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check if the user is an association admin
  const isAssociationAdmin = user?.is_association_admin && user?.role === 'subadmin';

  // Fetch status change requests
  useEffect(() => {
    const fetchStatusChanges = async () => {
      try {
        setLoading(true);
        const params = {
          page: pagination.current_page,
          per_page: pagination.per_page,
          status: statusFilter
        };
        
        const response = await api.get('/api/customers/status-changes', { params });
        setRequests(response.data.requests);
        setPagination({
          total: response.data.total,
          pages: response.data.pages,
          current_page: response.data.current_page,
          per_page: response.data.per_page
        });
      } catch (error) {
        console.error('Error fetching status change requests:', error);
        setError('Failed to load status change requests');
        toast.error('Failed to load status change requests');
      } finally {
        setLoading(false);
      }
    };

    fetchStatusChanges();
  }, [pagination.current_page, pagination.per_page, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPagination({
      ...pagination,
      current_page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({
      ...pagination,
      per_page: parseInt(event.target.value, 10),
      current_page: 1
    });
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPagination({
      ...pagination,
      current_page: 1
    });
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleActionClick = (request, actionType) => {
    setSelectedRequest(request);
    setAction(actionType);
    setRemarks('');
    setActionDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setActionDialogOpen(false);
  };

  const handleSubmitAction = async () => {
    if (!selectedRequest || !action) return;
    
    try {
      setSubmitting(true);
      
      await api.put(`/api/customers/status-change/${selectedRequest.id}`, {
        action: action,
        remarks: remarks
      });
      
      toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      
      // Refresh the list
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        status: statusFilter
      };
      
      const response = await api.get('/api/customers/status-changes', { params });
      setRequests(response.data.requests);
      
      setActionDialogOpen(false);
    } catch (error) {
      console.error('Error processing request:', error);
      toast.error(error.response?.data?.error || 'Failed to process request');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Box>
      <Typography variant="h4" mb={3}>Status Change Requests</Typography>
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Previous Status</TableCell>
                  <TableCell>Requested Status</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Processed By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No status change requests found</TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{new Date(request.requested_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button
                          color="primary"
                          onClick={() => handleViewCustomer(request.customer.id)}
                        >
                          {request.customer.name}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.previous_status.charAt(0).toUpperCase() + request.previous_status.slice(1)}
                          size="small"
                          variant="filled"
                          sx={{
                            ...(request.previous_status.toLowerCase() === 'normal' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...(['over exposure', 'over_exposure', 'overexposure'].includes(request.previous_status.toLowerCase()) && {
                              bgcolor: 'info.main',
                              color: 'info.contrastText',
                              '& .MuiChip-label': {
                                color: 'info.contrastText',
                              }
                            }),
                            ...(request.previous_status.toLowerCase() === 'defaulter' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...(request.previous_status.toLowerCase() === 'blacklisted' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.requested_status.charAt(0).toUpperCase() + request.requested_status.slice(1)}
                          size="small"
                          variant="filled"
                          sx={{
                            ...(request.requested_status.toLowerCase() === 'normal' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...(['over exposure', 'over_exposure', 'overexposure'].includes(request.requested_status.toLowerCase()) && {
                              bgcolor: 'info.main',
                              color: 'info.contrastText',
                              '& .MuiChip-label': {
                                color: 'info.contrastText',
                              }
                            }),
                            ...(request.requested_status.toLowerCase() === 'defaulter' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...(request.requested_status.toLowerCase() === 'blacklisted' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          size="small"
                          variant="filled"
                          sx={{
                            ...(request.status === 'approved' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...(request.status === 'pending' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...(request.status === 'rejected' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell>{request.requested_by.username}</TableCell>
                      <TableCell>{request.approved_by ? request.approved_by.username : '-'}</TableCell>
                      <TableCell>
                        {request.status === 'pending' && isAssociationAdmin ? (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => handleActionClick(request, 'approve')}
                                size="small"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => handleActionClick(request, 'reject')}
                                size="small"
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : request.remarks ? (
                          <Tooltip title={request.remarks}>
                            <IconButton size="small">
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={pagination.current_page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Approval/Rejection Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {action === 'approve' ? 'Approve Status Change Request' : 'Reject Status Change Request'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {action === 'approve'
              ? `Are you sure you want to approve the status change from ${selectedRequest?.previous_status} to ${selectedRequest?.requested_status} for customer ${selectedRequest?.customer.name}?`
              : `Are you sure you want to reject the status change from ${selectedRequest?.previous_status} to ${selectedRequest?.requested_status} for customer ${selectedRequest?.customer.name}?`}
          </DialogContentText>
          <TextField
            label="Remarks"
            multiline
            rows={4}
            fullWidth
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional: Add remarks about this decision"
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAction}
            variant="contained"
            color={action === 'approve' ? 'success' : 'error'}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (action === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatusChangesList;